import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, UserIcon } from 'lucide-react';
import Link from 'next/link';
import {
    //voteOnEditProposal, 
    vetoEditProposal,
    effectEditProposal
} from '@/af_actions/declarationActions';
import ExpandablePolicy from './ExpandingPolicy';
import { voteOnEditProposal, voteOnEditTitleProposal } from '@/actions/declarationActions';

interface AmendmentVoteCardProps {
    edit: any;
    paragraph: any;
    userId: string | undefined;
    isAdmin: boolean;
    resolutionBasePath: string;
    slug: string;
    onReplacement: (
        newContent: string,
        newTranslation: string,
        upvotes: number,
        downvotes: number,
        userVote: string | null,
        newStatus: string,
        newTitle: string,
        newRationale: string,
    ) => void;
    isGeneralAssembly: boolean;
    chamberType: string;
    isResolutionWhole?: boolean;
}

export const AmendmentVoteCard: React.FC<AmendmentVoteCardProps> = ({
    edit,
    paragraph,
    userId,
    isAdmin,
    resolutionBasePath,
    slug,
    onReplacement,
    isGeneralAssembly,
    chamberType = 'assembly',
    isResolutionWhole = false,
    
}) => {
    const [voteStats, setVoteStats] = useState({
        upvotes: edit.upvotes || 0,
        downvotes: edit.downvotes || 0,
        userVote: edit.userVote,
        status: edit.status || 'pending'
    });

    const handleVote = async (type: 'upvote' | 'downvote') => {
        if (!userId) {
            alert(code => "Sign in to vote.");
            return;
        }

        if (voteStats.status === 'vetoed') {
            alert(code => "Voting is not allowed on vetoed amendments.");
            return;
        }

        const currentVote = voteStats.userVote;
        let upDelta = 0;
        let downDelta = 0;

        if (currentVote === type) {
            type === 'upvote' ? (upDelta = -1) : (downDelta = -1);
        } else if (currentVote) {
            if (type === 'upvote') { upDelta = 1; downDelta = -1; }
            else { upDelta = -1; downDelta = 1; }
        } else {
            type === 'upvote' ? (upDelta = 1) : (downDelta = 1);
        }

        const newUpvotes = voteStats.upvotes + upDelta;
        const newDownvotes = voteStats.downvotes + downDelta;
        const newNetScore = newUpvotes - newDownvotes;
        const paragraphNetScore = (paragraph.vote_count || 0);
        const quorum = 1;


        const hasNoVotes = newUpvotes === 0 && newDownvotes === 0;

        const totalVotes = newUpvotes + newDownvotes;

        const quorumMet = totalVotes >= quorum;

        const majorityThreshold = quorumMet ? Math.floor(totalVotes / 2) + 1 : Math.floor(quorum / 2) + 1;
        let twoThirdsThreshold = quorumMet ? Math.ceil((totalVotes * 2) / 3) : Math.ceil((quorum * 2) / 3);

        // If the thresholds are equal, increment the two-thirds requirement by one
        if (twoThirdsThreshold === majorityThreshold) {
            //twoThirdsThreshold++;
        }



        let nextStatus = voteStats.status;
        if (newNetScore > paragraphNetScore) {
            nextStatus = voteStats.status === 'effected' ? 'effected' : 'accepted';
        } else if (newDownvotes >= newUpvotes) {
            nextStatus = 'rejected';
        } else if (hasNoVotes) {
            nextStatus = 'pending';
        } else if (newUpvotes > newDownvotes) {
            nextStatus = 'pending';
        }


        if (voteStats.status === "effected") {
            nextStatus = "effected"
        }

        if (quorumMet && newUpvotes >= twoThirdsThreshold) {
            if (isGeneralAssembly) {
                nextStatus = "tentatively_effected"
                if (voteStats.status === "effected") nextStatus = "effected"
            }

        }

        if (voteStats.status === "dealt") {
            alert("Voting is not allowed on historical amendments.");
            nextStatus = "dealt"

        }

        if (voteStats.status !== "dealt") {
            setVoteStats(prev => ({
                ...prev,
                upvotes: newUpvotes,
                downvotes: newDownvotes,
                userVote: currentVote === type ? null : type,
                status: nextStatus
            }));
        }
        try {
            if (!isResolutionWhole) {
                const result = await voteOnEditProposal(
                    edit.id,
                    type,
                    paragraph.id,
                    newNetScore,
                    nextStatus,
                    isGeneralAssembly,
                    chamberType
                );

                if (result?.replaced) {
                    onReplacement(
                        edit.proposed_content,
                        edit.proposed_translation,
                        newUpvotes,
                        newDownvotes,
                        currentVote === type ? null : type,
                        result.newStatus,//"accepted",
                        edit.proposed_title,
                        edit.rationale
                    );
                }

                if (result.editStatus) {
                    setVoteStats(prev => ({
                        ...prev,
                        status: result.editStatus
                    }));

                }

            } else if (isResolutionWhole) {
                const result = await voteOnEditTitleProposal(
                    edit.id,
                    type,
                    paragraph.id,
                    newNetScore,
                    nextStatus,
                    isGeneralAssembly,
                    chamberType
                );

                if (result?.replaced) {
                    onReplacement(
                        edit.proposed_content,
                        edit.proposed_translation,
                        newUpvotes,
                        newDownvotes,
                        currentVote === type ? null : type,
                        result.newStatus,//"accepted",
                        edit.proposed_title,
                        edit.rationale,
                    );
                }
            }
        } catch (e) {
            console.error(code => "Proposal vote failed", e);
        }
    };

    const handleAdminVeto = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            await vetoEditProposal(edit.id);
            setVoteStats(prev => ({ ...prev, status: 'vetoed' }));
        } catch (error) {
            console.error(code => "Veto failed", error);
        }
    };

    const handleAdminEffect = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            await effectEditProposal(edit.id, paragraph.id);
            setVoteStats(prev => ({ ...prev, status: 'dealt' }));
            onReplacement(
                edit.proposed_content,
                edit.proposed_translation,
                voteStats.upvotes,
                voteStats.downvotes,
                voteStats.userVote,
                "effected",
                edit.proposed_title
            );
        } catch (error) {
            console.error(code => "Effect failed", error);
        }
    };

    const netScore = voteStats.upvotes - voteStats.downvotes;
    const authorName = edit.author?.username?.username || edit.author?.username || 'Proposer';

    return (
        <div className="rounded-2xl bg-white p-4 shadow-sm border border-amber-100 hover:border-amber-300 transition-colors">
            <div className="mb-2">
                <Link
                    href={`${resolutionBasePath}/${slug}/${paragraph.position}/${edit.index}`}
                    className="text-[10px] font-bold text-amber-800 uppercase tracking-[0.2em] hover:underline"
                >
                    Amendment #{edit.index}. {edit.proposed_title && edit.proposed_title}
                </Link>
                <span></span>
            </div>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center">


                        {edit.author?.avatarUrl ? (
                            <img src={edit.author.avatarUrl} alt={authorName} className="h-full w-full object-cover" />
                        ) : (
                            <UserIcon size={14} className="text-slate-400" />
                        )}

                    </div>
                    <div className="flex flex-col">

                        <div>
                            {edit.author?.rank && (
                                <span className='px-1 bg-blue-100 rounded-full mr-1 uppercasea text-[12px]'>
                                    {edit.author?.rank === "Founding Father" ? "Founding Father" : edit.author?.rank}
                                </span>
                            )}
                            <Link href={`/profile/${authorName}`} className="text-xs font-bold text-slate-700 hover:underline">
                                {authorName}
                            </Link>

                        </div>
                        <div className="flex items-center gap-2">

                            {voteStats.status === "accepted" && (
                                <span className="text-[10px] text-green-400 font-bold uppercase tracking-wider">
                                    Favored
                                </span>
                            )}
                            {voteStats.status === "rejected" && (
                                <span className="text-[10px] text-red-400 font-bold uppercase tracking-wider">
                                    Rejected
                                </span>
                            )}

                            {voteStats.status === "vetoed" && (
                                <span className="text-[10px] text-red-600 font-bold uppercase tracking-wider">
                                    Vetoed
                                </span>
                            )}
                            {voteStats.status === "pending" && (
                                <span className="text-[10px] text-amber-600 font-bold uppercase tracking-wider">
                                    Under Consideration
                                </span>
                            )}
                            {voteStats.status === "tentatively_effected" && (
                                <span className="text-[10px] text-green-400 font-bold uppercase tracking-wider">
                                    Popularly Effective
                                </span>
                            )}
                            {voteStats.status === "dealt" && (
                                <span className="text-[10px] text-blue-400 font-bold uppercase tracking-wider">
                                    Historical
                                </span>
                            )}
                            {voteStats.status === "effected" && (
                                <span className="text-[10px] text-green-600 font-bold uppercase tracking-wider">
                                    Effective
                                </span>
                            )}
                            <div className="flex gap-1">
                                {isAdmin && voteStats.status !== 'vetoed' && voteStats.status !== 'effected' && paragraph.status === 'effected' && (
                                    <button
                                        onClick={handleAdminVeto}
                                        className="text-[9px] bg-red-800 text-white px-2 py-0.5 rounded-full hover:bg-red-900 transition-colors uppercase font-bold"
                                    >
                                        Veto
                                    </button>
                                )}
                                {isAdmin && voteStats.status !== 'vetoed' && voteStats.status !== 'effected' && paragraph.status === 'effected' &&
                                (voteStats.status === "accepted" || voteStats.status === "tentatively_effected") && (
                                    <button
                                        onClick={handleAdminEffect}
                                        className="text-[9px] bg-green-800 text-white px-2 py-0.5 rounded-full hover:bg-green-900 transition-colors uppercase font-bold"
                                    >
                                        Effect
                                    </button>
                                )}
                            </div>

                        </div>
                    </div>
                </div>

                <div className="flex items-center text-xs gap-2 bg-amber-50 px-2 py-1 rounded-lg border border-amber-100">
                    <button
                        onClick={() => handleVote('upvote')}
                        className={`p-1 rounded transition-colors ${voteStats.userVote === 'upvote' ? 'bg-green-100 text-green-600' : 'text-slate-400 hover:bg-green-100 hover:text-green-600'}`}
                    >
                        {isGeneralAssembly ? "Aye" : "Yea"}
                    </button>
                    <span className="text-xs font-bold text-slate-700 min-w-[1.5ch] text-center">{netScore}</span>
                    <button
                        onClick={() => handleVote('downvote')}
                        className={`p-1 rounded transition-colors ${voteStats.userVote === 'downvote' ? 'bg-red-100 text-red-600' : 'text-slate-400 hover:bg-red-100 hover:text-red-600'}`}
                    >
                        {isGeneralAssembly ? "No" : "Nay"}
                    </button>
                </div>
            </div>

            <Link href={`${resolutionBasePath}/${slug}/${paragraph.position}/${edit.index}`}>
                <div className="text-sm text-slate-800 mb-2">
                    <ExpandablePolicy content={edit.proposed_content || edit.proposed_translation || "Proposed amendment"} />
                </div>
                {edit.rationale && (
                    <div className="text-[10px] italic text-slate-500 bg-slate-50 p-2 rounded border border-slate-100">
                        {edit.rationale}
                    </div>
                )}
            </Link>
        </div>
    );
};